import type { LocaleCode, Difficulty } from "./api/client";
import type { CategoryId } from "@/data/dialogue-categories";

export interface DialogEdge {
  id: string;      // target node ID
}

export interface PhraseVariant {
  text: string;
  translation: string;
  vocalizationTip?: string; // phonetic approximation in learner's known script
}

export interface DialogNode {
  id: string;
  type: "start" | "option" | "response" | "end";
  text: string;           // primary/most common way to say it
  translation: string;
  vocalizationTip?: string; // phonetic approximation in learner's known script
  alternatives?: PhraseVariant[]; // other ways to express the same meaning
  children?: DialogEdge[]; // edges to other nodes
}

// Example with translation
export interface WordExample {
  text: string;               // Portuguese sentence
  translation: string;        // English translation
}

// A form entry with example and context
export interface FormEntry {
  form: string;               // the conjugated/variant form: "podemos"
  usage: string;              // when/how this form is used: "we can"
  vocalizationTip?: string;   // phonetic approximation in learner's known script
  example?: {                 // example sentence using this form
    sentence: string;         // "Podemos reunir-nos esta semana?"
    translation: string;      // "Can we meet this week?"
  };
  context?: {                 // conversational context showing before/after
    before?: string;          // previous line in conversation
    line: string;             // the line containing this form
    after?: string;           // response/next line
    situation: string;        // brief description: "Initiating a meeting request"
  };
}

// A word entry with all its forms (conjugations, variants)
export interface WordEntry {
  baseForm: string;           // dictionary form: "poder", "dia"
  translation: string;        // "to be able to / can", "day"
  pronunciation?: string;     // phonetic guide for base form
  vocalizationTip?: string;   // phonetic approximation in learner's known script
  partOfSpeech?: string;      // noun, verb, adjective, pronoun, preposition, etc.
  illustratable?: boolean;    // true for nouns, verbs, adjectives — BE-driven
  forms: FormEntry[];         // all forms that appear with usage, examples, context
  examples: WordExample[];    // general sentences from dialog with translations
}

// Component word in an expression
export interface ExpressionComponent {
  word: string;               // "jeito", "dá"
  meaning: string;            // "way/knack/skill", "gives"
  note?: string;              // optional grammar note: "3rd person singular of dar"
}

// An expression/idiom entry (multi-word)
export interface ExpressionEntry {
  expression: string;         // "lhe dá jeito", "se faz favor"
  translation: string;        // "works for you", "please"
  literal?: string;           // literal translation: "gives you fitting"
  vocalizationTip?: string;   // phonetic approximation in learner's known script
  components?: ExpressionComponent[]; // breakdown of individual words
  examples: WordExample[];    // sentences where it appears with translations
}

// Grammar rule with examples
export interface GrammarExample {
  target: string;            // Example in target language
  native: string;            // Translation in native language
  breakdown?: string;         // word-by-word breakdown showing the pattern
}

export interface GrammarRule {
  id: string;
  title: string;              // "Clitic Pronoun Placement"
  summary: string;            // Short explanation
  explanation: string;        // Detailed explanation
  pattern?: string;           // Formula like "Subject + Verb + Object" or "Verb-Pronoun"
  examples: GrammarExample[]; // Examples from dialog
  thinkingTip?: string;       // How to think about it / mental model
  commonMistake?: string;     // What English speakers often get wrong
  relatedTo?: string[];       // IDs of related rules
}

export interface GrammarCategory {
  category: string;           // "Word Order", "Pronouns", "Questions"
  rules: GrammarRule[];
}

// "How to Think" in a language - native mindset tips
export interface ThinkingExample {
  target: string;             // Example in target language
  literal: string;            // Literal/word-for-word translation
  natural: string;            // Natural equivalent in native language
  insight: string;            // What this reveals about native thinking
}

export interface ThinkingTip {
  id: string;
  title: string;              // "Think in Relationships, Not Actions"
  concept: string;            // Core concept explained simply
  whyItMatters: string;       // Why natives think this way
  examples: ThinkingExample[];
  practiceHint: string;       // How to practice this mindset
  commonTrap?: string;        // What learners often get wrong
}

export interface LanguageThinking {
  language: string;           // Display name (e.g. "Portuguese")
  introduction: string;       // Overview of how the language "thinks"
  keyInsights: string[];      // Quick bullet points
  tips: ThinkingTip[];
  // Locale metadata for locale-specific content
  targetLocale?: LocaleCode;  // BCP 47 code, e.g. "pt-BR"
  nativeLocale?: LocaleCode;  // BCP 47 code, e.g. "ru-RU"
}

// Topical vocabulary (numbers, days, etc. - supplementary)
export interface TopicalVocab {
  category: string;           // "Days of the Week", "Numbers", "Time"
  items: {
    word: string;
    translation: string;
    pronunciation?: string;
  }[];
}

// Building block - small common words needed to construct dialogue
// Now uses same structure as WordEntry for consistency
// partOfSpeech is inherited from WordEntry
export interface BuildingBlock extends WordEntry {}

// Complete vocabulary for a topic
export interface TopicVocabulary {
  words: WordEntry[];              // words derived from dialog with linked forms
  expressions: ExpressionEntry[];  // expressions/idioms from dialog
  topical: TopicalVocab[];         // supplementary topical vocab
  buildingBlocks?: BuildingBlock[]; // common grammar words (articles, prepositions, etc.)
}

export interface Phrase {
  phrase: string;
  translation: string;
  context: string;
}

export interface DialogTopic {
  id: string;
  title: string;
  description: string;
  /** Short 1-sentence summary */
  summary?: string;
  /** Original user prompt used to generate this topic */
  originalPrompt?: string;
  /** Category for grouping (food, transport, shopping, etc.) */
  category?: CategoryId;
  /** Opik trace ID for feedback correlation */
  traceId?: string;
  /** BCP 47 locale code (e.g. "pt-PT", "en-US") */
  language: LocaleCode;
  difficulty: Difficulty;
  speaker?: string;             // who the user is talking to (e.g., "Waiter", "Receptionist")
  vocabulary: TopicVocabulary;  // new structured vocabulary
  grammar: GrammarCategory[];   // grammar rules relevant to this topic
  phrases: Phrase[];
  dialogNodes: DialogNode[];
}

// Alphabet learning
export interface AlphabetLetter {
  letter: string;              // "A", "Ã", "CH"
  name: string;                // how to say the letter name: "á", "a til"
  sound: string;               // phonetic description: "ah" like in "father"
  examples: {
    word: string;              // example word
    translation: string;
    pronunciation: string;     // phonetic guide
  }[];
  notes?: string;              // special notes about usage
}

export interface AlphabetSection {
  title: string;               // "Basic Letters", "Accented Vowels", "Digraphs"
  description?: string;
  letters: AlphabetLetter[];
}

export interface LanguageAlphabet {
  language: string;
  sections: AlphabetSection[];
}

// Sample dialog data for European Portuguese
export const sampleTopics: DialogTopic[] = [
  {
    id: "scheduling-meeting",
    title: "Scheduling a Meeting",
    description: "Learn how to schedule appointments and meetings",
    language: "pt-PT",
    difficulty: "beginner",
    category: "work",
    vocabulary: {
      words: [
        {
          baseForm: "poder",
          translation: "to be able to / can",
          pronunciation: "poh-DEHR",
          forms: [
            {
              form: "poder",
              usage: "infinitive - to be able",
              example: { sentence: "Posso fazer uma exceção.", translation: "I can make an exception." },
            },
            {
              form: "podemos",
              usage: "we can",
              example: { sentence: "Podemos reunir-nos esta semana?", translation: "Can we meet this week?" },
              context: {
                line: "Olá, podemos reunir-nos esta semana?",
                after: "Sim, claro. Que dia lhe dá jeito?",
                situation: "Starting a conversation to schedule a meeting"
              }
            },
            {
              form: "pode",
              usage: "you (formal) / he/she can",
              example: { sentence: "Desculpe, pode repetir?", translation: "Sorry, can you repeat?" },
              context: {
                before: "Segunda-feira às 10?",
                line: "Desculpe, pode repetir?",
                after: "Segunda-feira às 10.",
                situation: "Asking someone to repeat what they said"
              }
            },
            {
              form: "poderia",
              usage: "could (conditional)",
              example: { sentence: "Poderia ser a outra hora?", translation: "Could it be at another time?" },
              context: {
                before: "Quarta-feira à tarde?",
                line: "Poderia ser a outra hora?",
                after: "Sim, claro. Que hora lhe dá jeito?",
                situation: "Politely suggesting a different time"
              }
            },
            {
              form: "posso",
              usage: "I can",
              example: { sentence: "Bem, posso fazer uma exceção.", translation: "Well, I can make an exception." },
              context: {
                before: "É urgente, só preciso de 15 minutos.",
                line: "Bem, posso fazer uma exceção.",
                after: "Perfeito, fica combinado.",
                situation: "Agreeing to help despite being busy"
              }
            },
          ],
          examples: [
            { text: "Olá, podemos reunir-nos esta semana?", translation: "Hi, can we meet this week?" },
            { text: "Poderia ser a outra hora?", translation: "Could it be at another time?" },
            { text: "Bem, posso fazer uma exceção.", translation: "Well, I can make an exception." },
          ],
        },
        {
          baseForm: "reunir",
          translation: "to meet / to gather",
          pronunciation: "heh-oo-NEER",
          forms: [
            {
              form: "reunir",
              usage: "infinitive - to meet",
              example: { sentence: "Precisamos de reunir.", translation: "We need to meet." },
            },
            {
              form: "reunir-nos",
              usage: "to meet (ourselves) - reflexive",
              example: { sentence: "Podemos reunir-nos esta semana?", translation: "Can we meet this week?" },
              context: {
                line: "Podemos reunir-nos esta semana?",
                after: "Sim, claro. Que dia lhe dá jeito?",
                situation: "Proposing a meeting"
              }
            },
            {
              form: "reunião",
              usage: "meeting (noun)",
              example: { sentence: "Temos uma reunião amanhã.", translation: "We have a meeting tomorrow." },
            },
          ],
          examples: [
            { text: "Podemos reunir-nos?", translation: "Can we meet?" },
            { text: "Temos uma reunião.", translation: "We have a meeting." },
          ],
        },
        {
          baseForm: "estar",
          translation: "to be (temporary state)",
          pronunciation: "esh-TAR",
          forms: [
            {
              form: "estar",
              usage: "infinitive - to be",
              example: { sentence: "Vou estar disponível amanhã.", translation: "I'll be available tomorrow." },
            },
            {
              form: "estou",
              usage: "I am",
              example: { sentence: "Esta semana estou muito ocupado.", translation: "This week I am very busy." },
              context: {
                before: "Podemos reunir-nos esta semana?",
                line: "Desculpe, esta semana estou muito ocupado.",
                after: "Que tal a próxima semana?",
                situation: "Declining because you're busy"
              }
            },
            {
              form: "está",
              usage: "you are (formal) / he/she is / it is",
              example: { sentence: "Quinta-feira está perfeito.", translation: "Thursday is perfect." },
              context: {
                before: "Tenho disponibilidade na quinta-feira.",
                line: "Quinta-feira está perfeito.",
                after: "Ótimo, até lá então!",
                situation: "Confirming a suggested day works"
              }
            },
            {
              form: "estamos",
              usage: "we are",
              example: { sentence: "Estamos de acordo.", translation: "We are in agreement." },
            },
          ],
          examples: [
            { text: "Esta semana estou muito ocupado.", translation: "This week I am very busy." },
            { text: "Quinta-feira está perfeito.", translation: "Thursday is perfect." },
          ],
        },
        {
          baseForm: "dar",
          translation: "to give",
          pronunciation: "dar",
          forms: [
            {
              form: "dar",
              usage: "infinitive - to give",
              example: { sentence: "Pode dar-me a informação?", translation: "Can you give me the information?" },
            },
            {
              form: "dá",
              usage: "gives / it gives",
              example: { sentence: "Que dia lhe dá jeito?", translation: "What day works for you?" },
              context: {
                before: "Sim, claro.",
                line: "Que dia lhe dá jeito?",
                after: "Segunda-feira às 10?",
                situation: "Asking which day suits the other person"
              }
            },
            {
              form: "dá-me",
              usage: "gives me / it suits me",
              example: { sentence: "Quarta-feira dá-me jeito.", translation: "Wednesday works for me." },
              context: {
                before: "Que dia lhe dá jeito?",
                line: "Quarta-feira dá-me jeito.",
                after: "Perfeito, fica combinado.",
                situation: "Stating which day works for you"
              }
            },
          ],
          examples: [
            { text: "Que dia lhe dá jeito?", translation: "What day works for you?" },
            { text: "Quarta-feira dá-me jeito.", translation: "Wednesday works for me." },
          ],
        },
        {
          baseForm: "ver",
          translation: "to see / to check",
          pronunciation: "vehr",
          forms: [
            {
              form: "ver",
              usage: "infinitive - to see",
              example: { sentence: "Deixe-me ver a minha agenda.", translation: "Let me check my schedule." },
              context: {
                before: "Podemos reunir-nos esta semana?",
                line: "Deixe-me ver a minha agenda.",
                after: "Tenho disponibilidade na quinta-feira.",
                situation: "Checking your schedule before committing"
              }
            },
            {
              form: "vejo",
              usage: "I see",
              example: { sentence: "Vejo que está ocupado.", translation: "I see that you're busy." },
            },
            {
              form: "vê",
              usage: "you see (formal) / he/she sees",
              example: { sentence: "Como vê, não tenho tempo.", translation: "As you can see, I don't have time." },
            },
          ],
          examples: [
            { text: "Deixe-me ver a minha agenda.", translation: "Let me check my schedule." },
          ],
        },
        {
          baseForm: "semana",
          translation: "week",
          pronunciation: "seh-MAH-nah",
          forms: [
            {
              form: "semana",
              usage: "week (singular)",
              example: { sentence: "Esta semana estou ocupado.", translation: "This week I'm busy." },
              context: {
                before: "Podemos reunir-nos?",
                line: "Esta semana estou muito ocupado.",
                after: "Que tal a próxima semana?",
                situation: "Explaining current week doesn't work"
              }
            },
          ],
          examples: [
            { text: "Esta semana estou ocupado.", translation: "This week I'm busy." },
            { text: "Que tal a próxima semana?", translation: "How about next week?" },
          ],
        },
        {
          baseForm: "ocupado",
          translation: "busy / occupied",
          pronunciation: "oh-koo-PAH-doo",
          forms: [
            {
              form: "ocupado",
              usage: "busy (masculine singular)",
              example: { sentence: "Estou muito ocupado.", translation: "I'm very busy." },
              context: {
                before: "Podemos reunir-nos esta semana?",
                line: "Desculpe, esta semana estou muito ocupado.",
                after: "Que tal a próxima semana?",
                situation: "Male speaker declining due to busy schedule"
              }
            },
            {
              form: "ocupada",
              usage: "busy (feminine singular)",
              example: { sentence: "Estou muito ocupada.", translation: "I'm very busy. (female speaker)" },
              context: {
                line: "Desculpe, esta semana estou muito ocupada.",
                situation: "Female speaker declining - note the -a ending"
              }
            },
            {
              form: "ocupados",
              usage: "busy (masculine plural)",
              example: { sentence: "Estamos todos ocupados.", translation: "We're all busy." },
            },
          ],
          examples: [
            { text: "Desculpe, esta semana estou muito ocupado.", translation: "Sorry, this week I'm very busy." },
            { text: "Também estou ocupado na próxima semana.", translation: "I'm also busy next week." },
          ],
        },
        {
          baseForm: "tempo",
          translation: "time",
          pronunciation: "TEM-poo",
          forms: [
            {
              form: "tempo",
              usage: "time (noun)",
              example: { sentence: "Não tenho tempo livre.", translation: "I don't have any free time." },
              context: {
                before: "É urgente, só preciso de 15 minutos.",
                line: "A sério, não tenho tempo.",
                after: "Compreendo. Entramos em contacto mais tarde.",
                situation: "Firmly declining when someone insists"
              }
            },
          ],
          examples: [
            { text: "Não tenho tempo livre.", translation: "I don't have any free time." },
            { text: "A sério, não tenho tempo.", translation: "Really, I don't have time." },
          ],
        },
        {
          baseForm: "hora",
          translation: "hour / time",
          pronunciation: "OH-rah",
          forms: [
            {
              form: "hora",
              usage: "hour (singular)",
              example: { sentence: "Poderia ser a outra hora?", translation: "Could it be at another time?" },
              context: {
                before: "Segunda-feira às 10?",
                line: "Poderia ser a outra hora?",
                after: "Claro, que hora lhe dá jeito?",
                situation: "Requesting a different time"
              }
            },
            {
              form: "horas",
              usage: "hours / o'clock (plural)",
              example: { sentence: "A que horas?", translation: "At what time?" },
              context: {
                before: "Quarta-feira dá-me jeito.",
                line: "A que horas?",
                after: "Às 10 da manhã?",
                situation: "Asking to specify the time"
              }
            },
          ],
          examples: [
            { text: "A que horas?", translation: "At what time?" },
            { text: "Poderia ser a outra hora?", translation: "Could it be at another time?" },
          ],
        },
      ],
      expressions: [
        {
          expression: "lhe dá jeito",
          translation: "works for you / suits you",
          literal: "gives you fitting",
          components: [
            { word: "lhe", meaning: "to you (formal)", note: "indirect object pronoun" },
            { word: "dá", meaning: "gives", note: "3rd person singular of 'dar'" },
            { word: "jeito", meaning: "way / knack / fit", note: "noun - can mean skill, way, or suitability" },
          ],
          examples: [
            { text: "Que dia lhe dá jeito?", translation: "What day works for you?" },
            { text: "Quarta-feira dá-me jeito.", translation: "Wednesday works for me." },
          ],
        },
        {
          expression: "fica combinado",
          translation: "it's settled / agreed",
          literal: "stays combined",
          components: [
            { word: "fica", meaning: "stays / remains", note: "3rd person singular of 'ficar'" },
            { word: "combinado", meaning: "agreed / arranged", note: "past participle of 'combinar'" },
          ],
          examples: [
            { text: "Perfeito, fica combinado.", translation: "Perfect, it's settled then." },
            { text: "Excelente, combinado então.", translation: "Excellent, agreed then." },
          ],
        },
        {
          expression: "deixe-me ver",
          translation: "let me see / let me check",
          literal: "leave me to see",
          components: [
            { word: "deixe", meaning: "let / allow", note: "imperative of 'deixar'" },
            { word: "me", meaning: "me", note: "direct object pronoun" },
            { word: "ver", meaning: "to see / to check", note: "infinitive verb" },
          ],
          examples: [
            { text: "Deixe-me ver a minha agenda.", translation: "Let me check my schedule." },
          ],
        },
        {
          expression: "fazer uma exceção",
          translation: "to make an exception",
          literal: "to make an exception",
          components: [
            { word: "fazer", meaning: "to make / to do", note: "infinitive verb" },
            { word: "uma", meaning: "a / one", note: "feminine indefinite article" },
            { word: "exceção", meaning: "exception", note: "noun (feminine)" },
          ],
          examples: [
            { text: "Bem, posso fazer uma exceção.", translation: "Well, I can make an exception." },
          ],
        },
        {
          expression: "a sério",
          translation: "seriously / really",
          literal: "in serious",
          components: [
            { word: "a", meaning: "in / at", note: "preposition" },
            { word: "sério", meaning: "serious", note: "adjective" },
          ],
          examples: [
            { text: "A sério, não tenho tempo.", translation: "Really, I don't have time." },
          ],
        },
        {
          expression: "pode repetir",
          translation: "can you repeat",
          literal: "can repeat",
          components: [
            { word: "pode", meaning: "can / is able to", note: "3rd person singular of 'poder'" },
            { word: "repetir", meaning: "to repeat", note: "infinitive verb" },
          ],
          examples: [
            { text: "Desculpe, pode repetir?", translation: "Sorry, can you repeat that?" },
          ],
        },
      ],
      topical: [
        {
          category: "Days of the Week",
          items: [
            { word: "segunda-feira", translation: "Monday", pronunciation: "seh-GOON-dah FAY-rah" },
            { word: "terça-feira", translation: "Tuesday", pronunciation: "TEHR-sah FAY-rah" },
            { word: "quarta-feira", translation: "Wednesday", pronunciation: "KWAR-tah FAY-rah" },
            { word: "quinta-feira", translation: "Thursday", pronunciation: "KEEN-tah FAY-rah" },
            { word: "sexta-feira", translation: "Friday", pronunciation: "SAYSH-tah FAY-rah" },
          ],
        },
        {
          category: "Time of Day",
          items: [
            { word: "manhã", translation: "morning", pronunciation: "mah-NYAH" },
            { word: "tarde", translation: "afternoon", pronunciation: "TAR-deh" },
            { word: "noite", translation: "evening/night", pronunciation: "NOY-teh" },
          ],
        },
        {
          category: "Numbers (for times)",
          items: [
            { word: "dez", translation: "ten (10)", pronunciation: "dehsh" },
            { word: "quinze", translation: "fifteen (15)", pronunciation: "KEEN-zeh" },
            { word: "meia", translation: "half (30)", pronunciation: "MAY-ah" },
          ],
        },
      ],
    },
    grammar: [
      {
        category: "Word Order",
        rules: [
          {
            id: "svo-basic",
            title: "Basic Sentence Order (SVO)",
            summary: "Portuguese follows Subject-Verb-Object order, like English.",
            explanation: "The basic Portuguese sentence follows the same Subject-Verb-Object pattern as English. However, the subject pronoun is often dropped because the verb conjugation already indicates who is doing the action.",
            pattern: "(Subject) + Verb + Object",
            examples: [
              {
                target: "Podemos reunir-nos esta semana?",
                native: "Can we meet this week?",
                breakdown: "(Nós) podemos reunir-nos esta semana? → (We) can meet this week?"
              },
              {
                target: "Tenho disponibilidade na quinta-feira.",
                native: "I have availability on Thursday.",
                breakdown: "(Eu) tenho disponibilidade → (I) have availability"
              }
            ],
            thinkingTip: "Think of it like English, but you can drop 'I', 'you', 'we' because the verb ending already tells you who's doing it.",
            commonMistake: "Don't always include the subject pronoun – it sounds unnatural. 'Eu tenho tempo' is correct but 'Tenho tempo' is more natural."
          },
          {
            id: "question-inversion",
            title: "Questions Without Inversion",
            summary: "Portuguese questions often keep the same word order as statements – just add a question mark and rising intonation.",
            explanation: "Unlike English, Portuguese doesn't require inverting subject and verb to form questions. You can turn a statement into a question just by changing your intonation (rising at the end).",
            pattern: "Statement word order + ? (rising intonation)",
            examples: [
              {
                target: "Tem reserva?",
                native: "Do you have a reservation?",
                breakdown: "Tem reserva. (statement) → Tem reserva? (question) – same words!"
              },
              {
                target: "Podemos reunir-nos?",
                native: "Can we meet?",
                breakdown: "No 'do' helper needed – just raise your voice at the end"
              }
            ],
            thinkingTip: "Forget about 'do/does' for questions. Just say the statement and raise your pitch at the end.",
            commonMistake: "Don't try to translate 'Do you have...?' word-for-word. There's no 'do' in Portuguese questions."
          }
        ]
      },
      {
        category: "Pronouns & Clitics",
        rules: [
          {
            id: "clitic-placement",
            title: "Clitic Pronoun Placement",
            summary: "Short pronouns (me, te, lhe, nos) attach to verbs with hyphens or come before them.",
            explanation: "In European Portuguese, object pronouns usually come AFTER the verb and attach with a hyphen. But in questions, negatives, and some other cases, they come BEFORE the verb.",
            pattern: "Verb-pronoun (statements) | Pronoun + verb (questions/negatives)",
            examples: [
              {
                target: "Deixe-me ver",
                native: "Let me see",
                breakdown: "Deixe (let) + me (me) + ver (see) → verb-pronoun in commands"
              },
              {
                target: "Quarta-feira dá-me jeito",
                native: "Wednesday works for me",
                breakdown: "dá (gives) + me (me) → verb-pronoun in statements"
              },
              {
                target: "Que dia lhe dá jeito?",
                native: "What day works for you?",
                breakdown: "lhe (to you) + dá → pronoun before verb in questions"
              }
            ],
            thinkingTip: "In questions and negatives, pronouns jump in front of the verb. In regular statements and commands, they stick to the end with a hyphen.",
            commonMistake: "Brazilian Portuguese often puts pronouns before the verb even in statements. European Portuguese prefers them after."
          },
          {
            id: "reflexive-verbs",
            title: "Reflexive Verbs (-se / -nos)",
            summary: "Many verbs include a reflexive pronoun to show the action affects the subject.",
            explanation: "Reflexive verbs use pronouns like 'se' (oneself), 'me' (myself), 'nos' (ourselves) to show the action reflects back. 'Reunir-nos' means 'to meet' (literally 'to gather ourselves').",
            pattern: "Verb + reflexive pronoun",
            examples: [
              {
                target: "Podemos reunir-nos esta semana?",
                native: "Can we meet this week?",
                breakdown: "reunir (to gather) + nos (ourselves) = to meet (each other)"
              }
            ],
            thinkingTip: "Think of reflexive verbs as 'doing something to/with ourselves'. Reunir-nos = gather ourselves together = meet up.",
            commonMistake: "Don't forget the reflexive pronoun – 'podemos reunir' sounds incomplete."
          }
        ]
      },
      {
        category: "Time & Dates",
        rules: [
          {
            id: "time-telling",
            title: "Telling Time",
            summary: "Use 'às' (at) + hour. Half past uses 'e meia'. Quarter uses 'e um quarto'.",
            explanation: "Times use the feminine article because 'hora' (hour) is feminine. 'Às dez' = at ten. For half past, add 'e meia' (and half). Minutes can be added with 'e' (and).",
            pattern: "às + [hour] (e meia / e [minutes])",
            examples: [
              {
                target: "Segunda-feira às 10?",
                native: "Monday at 10?",
                breakdown: "às = at (feminine plural, agreeing with 'horas')"
              },
              {
                target: "A que horas?",
                native: "At what time?",
                breakdown: "A que = at what, horas = hours/time"
              }
            ],
            thinkingTip: "'Às' is a contraction of 'a' (at) + 'as' (the, feminine plural). Think: at-the-hours.",
            commonMistake: "Don't say 'em dez' for 'at ten' – use 'às dez'."
          },
          {
            id: "days-of-week",
            title: "Days of the Week",
            summary: "Days use '-feira' (fair/market day). Use 'na' for 'on' with days.",
            explanation: "Portuguese weekdays come from medieval market days: segunda-feira (second fair), terça-feira (third fair), etc. Use 'na' (em + a) for 'on': na segunda-feira.",
            pattern: "na + [day]-feira",
            examples: [
              {
                target: "Quarta-feira à tarde?",
                native: "Wednesday afternoon?",
                breakdown: "quarta (fourth) + feira (fair) = Wednesday (4th market day)"
              },
              {
                target: "Tenho disponibilidade na quinta-feira.",
                native: "I have availability on Thursday.",
                breakdown: "na = on the (em + a), quinta-feira = Thursday"
              }
            ],
            thinkingTip: "Count from Sunday: Sunday (domingo), then 2nd-feira (Monday), 3rd-feira (Tuesday)... up to 6th-feira (Friday). Saturday breaks the pattern (sábado).",
            commonMistake: "Remember the hyphen: it's 'segunda-feira', not 'segunda feira'."
          }
        ]
      },
      {
        category: "Politeness & Formality",
        rules: [
          {
            id: "formal-you",
            title: "Formal 'You' (o senhor / a senhora)",
            summary: "Use 3rd person verb forms for polite address. The pronoun is often dropped.",
            explanation: "Portuguese has no direct equivalent to formal 'you'. Instead, use 'o senhor' (the gentleman) or 'a senhora' (the lady) with 3rd person verbs. In practice, the title is often dropped and you just use 3rd person forms.",
            pattern: "(O senhor/A senhora) + 3rd person verb",
            examples: [
              {
                target: "Sou flexível, escolha o senhor.",
                native: "I'm flexible, you choose (sir).",
                breakdown: "escolha = 'choose' (3rd person/formal command)"
              },
              {
                target: "Que dia lhe dá jeito?",
                native: "What day works for you?",
                breakdown: "lhe = to you (formal), using 3rd person indirect object"
              }
            ],
            thinkingTip: "Think of it as speaking about someone respectfully: instead of 'Can YOU...', it's like 'Can the gentleman...' but smoother.",
            commonMistake: "Don't use 'tu' (informal you) in professional or formal settings – stick with 3rd person forms."
          }
        ]
      }
    ],
    phrases: [
      {
        phrase: "Podemos reunir-nos?",
        translation: "Can we meet?",
        context: "Initiating a meeting request",
      },
      {
        phrase: "Que dia lhe dá jeito?",
        translation: "What day works for you?",
        context: "Asking about availability",
      },
      {
        phrase: "A que horas?",
        translation: "At what time?",
        context: "Asking about time",
      },
      {
        phrase: "Perfeito, fica combinado.",
        translation: "Perfect, it's settled then.",
        context: "Confirming the appointment",
      },
    ],
    dialogNodes: [
      {
        id: "start",
        type: "start",
        text: "Olá, podemos reunir-nos esta semana?",
        translation: "Hi, can we meet this week?",
        alternatives: [
          { text: "Bom dia, será que conseguimos marcar uma reunião?", translation: "Good morning, can we schedule a meeting?" },
          { text: "Boa tarde, teria disponibilidade para nos encontrarmos?", translation: "Good afternoon, would you be available to meet?" },
          { text: "Desculpe incomodar, podíamos combinar uma reunião?", translation: "Sorry to bother you, could we arrange a meeting?" },
        ],
        children: [
          { id: "positive-response" },
          { id: "negative-response" },
          { id: "check-schedule" },
        ],
      },
      {
        id: "positive-response",
        type: "option",
        text: "Sim, claro. Que dia lhe dá jeito?",
        translation: "Yes, of course. What day works for you?",
        alternatives: [
          { text: "Com certeza. Quando é que lhe convém?", translation: "Certainly. When is convenient for you?" },
          { text: "Claro que sim. Qual é a sua disponibilidade?", translation: "Of course. What's your availability?" },
        ],
        children: [
          { id: "suggest-monday" },
          { id: "suggest-wednesday" },
          { id: "flexible" },
        ],
      },
      {
        id: "negative-response",
        type: "option",
        text: "Desculpe, esta semana estou muito ocupado.",
        translation: "I'm sorry, I'm very busy this week.",
        alternatives: [
          { text: "Infelizmente não tenho tempo esta semana.", translation: "Unfortunately I don't have time this week." },
          { text: "Esta semana está complicado para mim.", translation: "This week is difficult for me." },
        ],
        children: [
          { id: "suggest-next-week" },
          { id: "insist-urgent" },
        ],
      },
      {
        id: "check-schedule",
        type: "option",
        text: "Deixe-me ver a minha agenda.",
        translation: "Let me check my schedule.",
        alternatives: [
          { text: "Um momento, vou verificar a minha disponibilidade.", translation: "One moment, I'll check my availability." },
          { text: "Espere, deixe-me consultar o meu calendário.", translation: "Wait, let me check my calendar." },
        ],
        children: [
          { id: "found-time" },
          { id: "no-time" },
        ],
      },
      {
        id: "suggest-monday",
        type: "response",
        text: "Segunda-feira às 10?",
        translation: "Monday at 10?",
        children: [
          { id: "confirm-time" },
          { id: "suggest-another-time" },
          { id: "ask-repeat" },
        ],
      },
      {
        id: "suggest-wednesday",
        type: "response",
        text: "Quarta-feira à tarde?",
        translation: "Wednesday afternoon?",
        children: [
          { id: "confirm-time" },
          { id: "suggest-another-time" },
          { id: "ask-repeat" },
        ],
      },
      {
        id: "flexible",
        type: "response",
        text: "Sou flexível, escolha o senhor.",
        translation: "I'm flexible, you choose.",
        children: [
          { id: "suggest-monday" },
          { id: "suggest-wednesday" },
        ],
      },
      {
        id: "suggest-next-week",
        type: "response",
        text: "Que tal a próxima semana?",
        translation: "How about next week?",
        children: [
          { id: "accept-next-week" },
          { id: "still-busy" },
        ],
      },
      {
        id: "insist-urgent",
        type: "response",
        text: "É urgente, só preciso de 15 minutos.",
        translation: "It's urgent, I only need 15 minutes.",
        children: [
          { id: "make-exception" },
          { id: "really-no-time" },
        ],
      },
      {
        id: "found-time",
        type: "response",
        text: "Tenho disponibilidade na quinta-feira.",
        translation: "I have availability on Thursday.",
        children: [
          { id: "confirm-time" },
          { id: "suggest-another-time" },
        ],
      },
      {
        id: "no-time",
        type: "response",
        text: "Não tenho tempo livre esta semana.",
        translation: "I don't have any slots this week.",
        children: [{ id: "suggest-next-week" }],
      },
      {
        id: "confirm-time",
        type: "option",
        text: "Perfeito, fica combinado.",
        translation: "Perfect, it's settled then.",
        alternatives: [
          { text: "Ótimo, está marcado.", translation: "Great, it's set." },
          { text: "Excelente, combinado então.", translation: "Excellent, agreed then." },
          { text: "Muito bem, fico à espera.", translation: "Very well, I'll be waiting." },
        ],
        children: [{ id: "end-success" }],
      },
      {
        id: "suggest-another-time",
        type: "option",
        text: "Poderia ser a outra hora?",
        translation: "Could it be at another time?",
        alternatives: [
          { text: "Essa hora não me dá jeito. Outra hora?", translation: "That time doesn't work for me. Another time?" },
          { text: "Seria possível mais tarde?", translation: "Would later be possible?" },
        ],
        children: [{ id: "positive-response" }],
      },
      {
        id: "ask-repeat",
        type: "option",
        text: "Desculpe, pode repetir?",
        translation: "Sorry, can you repeat that?",
        alternatives: [
          { text: "Não percebi, pode dizer outra vez?", translation: "I didn't understand, can you say that again?" },
          { text: "Como disse? Não ouvi bem.", translation: "What did you say? I didn't hear well." },
        ],
        children: [
          { id: "suggest-monday" },
          { id: "suggest-wednesday" },
        ],
      },
      {
        id: "accept-next-week",
        type: "option",
        text: "Sim, a próxima semana é perfeito.",
        translation: "Yes, next week is perfect.",
        children: [{ id: "positive-response" }],
      },
      {
        id: "still-busy",
        type: "option",
        text: "Também estou ocupado na próxima semana.",
        translation: "I'm also busy next week.",
        children: [{ id: "end-fail" }],
      },
      {
        id: "make-exception",
        type: "option",
        text: "Bem, posso fazer uma exceção.",
        translation: "Well, I can make an exception.",
        children: [{ id: "confirm-time" }],
      },
      {
        id: "really-no-time",
        type: "option",
        text: "A sério, não tenho tempo.",
        translation: "Really, I don't have time.",
        children: [{ id: "end-fail" }],
      },
      {
        id: "end-success",
        type: "end",
        text: "Ótimo, até lá então!",
        translation: "Great, see you then!",
      },
      {
        id: "end-fail",
        type: "end",
        text: "Compreendo. Entramos em contacto mais tarde.",
        translation: "I understand. We'll be in touch later.",
      },
    ],
  },
  {
    id: "ordering-food",
    title: "Ordering at a Restaurant",
    description: "Practice ordering food and drinks",
    language: "pt-PT",
    difficulty: "beginner",
    category: "food",
    vocabulary: {
      words: [
        {
          baseForm: "ter",
          translation: "to have",
          pronunciation: "tehr",
          forms: [
            {
              form: "ter",
              usage: "infinitive - to have",
              example: { sentence: "Preciso de ter uma reserva?", translation: "Do I need to have a reservation?" },
            },
            {
              form: "tem",
              usage: "you have (formal) / he/she has",
              example: { sentence: "Tem reserva?", translation: "Do you have a reservation?" },
              context: {
                before: "Bom dia, têm mesa disponível?",
                line: "Tem reserva?",
                after: "Não, é necessário?",
                situation: "Waiter asking if you made a reservation"
              }
            },
            {
              form: "têm",
              usage: "they have / you (plural, formal) have",
              example: { sentence: "Têm mesa disponível?", translation: "Do you have a table available?" },
              context: {
                line: "Bom dia, têm mesa disponível?",
                after: "Sim, para quantas pessoas?",
                situation: "Asking a restaurant if they have tables"
              }
            },
            {
              form: "tenho",
              usage: "I have",
              example: { sentence: "Tenho uma reserva.", translation: "I have a reservation." },
            },
          ],
          examples: [
            { text: "Têm mesa disponível?", translation: "Do you have a table available?" },
            { text: "Tem reserva?", translation: "Do you have a reservation?" },
          ],
        },
        {
          baseForm: "mesa",
          translation: "table",
          pronunciation: "MEH-zah",
          forms: [
            {
              form: "mesa",
              usage: "table (singular)",
              example: { sentence: "Uma mesa para dois, se faz favor.", translation: "A table for two, please." },
              context: {
                before: "Sim, para quantas pessoas?",
                line: "Uma mesa para dois, se faz favor.",
                after: "Por aqui, se faz favor.",
                situation: "Requesting a table for your party"
              }
            },
            {
              form: "mesas",
              usage: "tables (plural)",
              example: { sentence: "Têm mesas no terraço?", translation: "Do you have tables on the terrace?" },
            },
          ],
          examples: [
            { text: "Uma mesa para dois, se faz favor.", translation: "A table for two, please." },
            { text: "Têm mesa disponível?", translation: "Do you have a table available?" },
          ],
        },
        {
          baseForm: "esperar",
          translation: "to wait",
          pronunciation: "esh-peh-RAR",
          forms: [
            {
              form: "esperar",
              usage: "infinitive - to wait",
              example: { sentence: "Vamos ter de esperar.", translation: "We're going to have to wait." },
            },
            {
              form: "espera",
              usage: "wait (noun) / he/she waits",
              example: { sentence: "Há uma espera de 15 minutos.", translation: "There's a 15-minute wait." },
              context: {
                before: "Têm mesa disponível?",
                line: "Há uma espera de 15 minutos.",
                after: "Está bem, esperamos.",
                situation: "Being told there's a wait for a table"
              }
            },
            {
              form: "esperamos",
              usage: "we wait",
              example: { sentence: "Está bem, esperamos.", translation: "That's fine, we'll wait." },
              context: {
                before: "Há uma espera de 15 minutos.",
                line: "Está bem, esperamos.",
                after: "Por aqui, se faz favor.",
                situation: "Agreeing to wait for a table"
              }
            },
            {
              form: "espere",
              usage: "wait! (imperative)",
              example: { sentence: "Espere um momento.", translation: "Wait a moment." },
            },
          ],
          examples: [
            { text: "Há uma espera de 15 minutos.", translation: "There's a 15-minute wait." },
            { text: "Está bem, esperamos.", translation: "That's fine, we'll wait." },
          ],
        },
        {
          baseForm: "voltar",
          translation: "to return / come back",
          pronunciation: "vohl-TAR",
          forms: [
            {
              form: "voltar",
              usage: "infinitive - to return",
              example: { sentence: "Podemos voltar mais tarde.", translation: "We can come back later." },
            },
            {
              form: "voltamos",
              usage: "we return",
              example: { sentence: "Voltamos mais tarde.", translation: "We'll come back later." },
              context: {
                before: "Há uma espera de 15 minutos.",
                line: "Voltamos mais tarde.",
                after: "Até logo.",
                situation: "Deciding to return later instead of waiting"
              }
            },
            {
              form: "volta",
              usage: "he/she returns",
              example: { sentence: "O empregado volta já.", translation: "The waiter will be right back." },
            },
          ],
          examples: [
            { text: "Voltamos mais tarde.", translation: "We'll come back later." },
          ],
        },
        {
          baseForm: "reserva",
          translation: "reservation",
          pronunciation: "heh-ZEHR-vah",
          forms: [
            {
              form: "reserva",
              usage: "reservation (noun)",
              example: { sentence: "Tem reserva?", translation: "Do you have a reservation?" },
              context: {
                before: "Bom dia, têm mesa disponível?",
                line: "Tem reserva?",
                after: "Sim, em nome de Silva.",
                situation: "Restaurant checking for your reservation"
              }
            },
          ],
          examples: [
            { text: "Tem reserva?", translation: "Do you have a reservation?" },
            { text: "Sim, em nome de Silva.", translation: "Yes, under the name Silva." },
          ],
        },
      ],
      expressions: [
        {
          expression: "se faz favor",
          translation: "please",
          literal: "if it does favor",
          components: [
            { word: "se", meaning: "if", note: "conjunction" },
            { word: "faz", meaning: "does / makes", note: "3rd person singular of 'fazer'" },
            { word: "favor", meaning: "favor", note: "noun (masculine)" },
          ],
          examples: [
            { text: "Uma mesa para dois, se faz favor.", translation: "A table for two, please." },
            { text: "Por aqui, se faz favor.", translation: "This way, please." },
          ],
        },
        {
          expression: "em nome de",
          translation: "under the name of",
          literal: "in name of",
          components: [
            { word: "em", meaning: "in", note: "preposition" },
            { word: "nome", meaning: "name", note: "noun (masculine)" },
            { word: "de", meaning: "of", note: "preposition" },
          ],
          examples: [
            { text: "Sim, em nome de Silva.", translation: "Yes, under the name Silva." },
          ],
        },
        {
          expression: "bom apetite",
          translation: "enjoy your meal",
          literal: "good appetite",
          components: [
            { word: "bom", meaning: "good", note: "adjective (masculine)" },
            { word: "apetite", meaning: "appetite", note: "noun (masculine)" },
          ],
          examples: [
            { text: "Bom apetite!", translation: "Enjoy your meal!" },
          ],
        },
        {
          expression: "mais tarde",
          translation: "later",
          literal: "more late",
          components: [
            { word: "mais", meaning: "more", note: "adverb" },
            { word: "tarde", meaning: "late / afternoon", note: "adverb or noun" },
          ],
          examples: [
            { text: "Voltamos mais tarde.", translation: "We'll come back later." },
          ],
        },
      ],
      topical: [
        {
          category: "Numbers (for people)",
          items: [
            { word: "um/uma", translation: "one", pronunciation: "oom/OO-mah" },
            { word: "dois/duas", translation: "two", pronunciation: "doysh/DOO-ahsh" },
            { word: "três", translation: "three", pronunciation: "trehsh" },
            { word: "quatro", translation: "four", pronunciation: "KWA-troo" },
            { word: "cinco", translation: "five", pronunciation: "SEEN-koo" },
          ],
        },
        {
          category: "Restaurant Items",
          items: [
            { word: "ementa", translation: "menu", pronunciation: "eh-MEN-tah" },
            { word: "conta", translation: "bill/check", pronunciation: "KOHN-tah" },
            { word: "gorjeta", translation: "tip", pronunciation: "gohr-ZHEH-tah" },
          ],
        },
      ],
    },
    grammar: [
      {
        category: "Numbers & Gender",
        rules: [
          {
            id: "number-gender",
            title: "Numbers and Gender Agreement",
            summary: "Numbers 1 and 2 change form to match the gender of what they're counting.",
            explanation: "Unlike English, Portuguese numbers 'one' and 'two' have masculine and feminine forms. Use 'um/dois' for masculine nouns and 'uma/duas' for feminine nouns. Numbers 3+ don't change.",
            pattern: "um/uma (1), dois/duas (2), três, quatro... (3+)",
            examples: [
              {
                target: "Uma mesa para dois",
                native: "A table for two",
                breakdown: "uma (one, fem.) mesa (table, fem.) para dois (for two, masc. - referring to people)"
              },
              {
                target: "Para duas pessoas",
                native: "For two people",
                breakdown: "duas (two, fem.) pessoas (people, fem.) – 'pessoas' is feminine"
              },
              {
                target: "Somos um grupo de cinco",
                native: "We're a group of five",
                breakdown: "um (one, masc.) grupo (group, masc.), cinco (five – no gender change)"
              }
            ],
            thinkingTip: "Only worry about gender for 1 and 2. From 3 onwards, numbers stay the same. Check if the noun ends in -a (usually feminine) or -o (usually masculine).",
            commonMistake: "Don't say 'dois pessoas' – pessoas is feminine, so it's 'duas pessoas'."
          }
        ]
      },
      {
        category: "Questions & Requests",
        rules: [
          {
            id: "ter-questions",
            title: "Questions with 'Ter' (to have)",
            summary: "'Ter' is used for 'have' questions. No 'do' helper needed.",
            explanation: "To ask 'Do you have...?', just use the verb 'ter' conjugated appropriately. 'Tem' is formal singular, 'Têm' is plural. No auxiliary verb like 'do' is needed.",
            pattern: "Tem/Têm + [noun] + ?",
            examples: [
              {
                target: "Têm mesa disponível?",
                native: "Do you have a table available?",
                breakdown: "Têm (you-plural have) + mesa (table) + disponível (available)"
              },
              {
                target: "Tem reserva?",
                native: "Do you have a reservation?",
                breakdown: "Tem (you-formal have) + reserva (reservation)"
              }
            ],
            thinkingTip: "Just say 'Have table?' with rising intonation. Portuguese doesn't need the 'Do you' part.",
            commonMistake: "Don't try to translate 'Do you have' – there's no word for 'do' in Portuguese questions."
          },
          {
            id: "para-for",
            title: "Using 'Para' (for)",
            summary: "'Para' means 'for' when indicating purpose or recipient.",
            explanation: "'Para' is used to express 'for' in the sense of purpose, destination, or recipient. In restaurant contexts, use it to specify how many people or what you want something for.",
            pattern: "para + [number/person/purpose]",
            examples: [
              {
                target: "Uma mesa para dois, se faz favor",
                native: "A table for two, please",
                breakdown: "para dois = for two (people)"
              },
              {
                target: "Para quantas pessoas?",
                native: "For how many people?",
                breakdown: "para quantas = for how many"
              }
            ],
            thinkingTip: "'Para' = for (purpose/recipient). Think of it as answering 'for what?' or 'for whom?'",
            commonMistake: "Don't confuse 'para' (for) with 'por' (by/through) – they're different prepositions."
          }
        ]
      },
      {
        category: "Politeness",
        rules: [
          {
            id: "se-faz-favor",
            title: "'Se faz favor' vs 'Por favor'",
            summary: "Both mean 'please' but 'se faz favor' is more European Portuguese.",
            explanation: "'Se faz favor' (literally 'if it does favor') is the traditional European Portuguese way to say 'please'. 'Por favor' is also used and understood everywhere. Both are polite.",
            pattern: "[request] + se faz favor / por favor",
            examples: [
              {
                target: "Uma mesa para dois, se faz favor.",
                native: "A table for two, please.",
                breakdown: "se faz favor = please (European Portuguese style)"
              },
              {
                target: "Por aqui, se faz favor.",
                native: "This way, please.",
                breakdown: "Used by staff when guiding you"
              }
            ],
            thinkingTip: "'Se faz favor' sounds more traditionally European Portuguese. 'Por favor' is more universal and also fine to use.",
            commonMistake: "Both are correct – don't worry about choosing wrong. 'Se faz favor' might sound slightly more local in Portugal."
          }
        ]
      },
      {
        category: "Verbs",
        rules: [
          {
            id: "present-future",
            title: "Present Tense for Future",
            summary: "Portuguese often uses present tense to express future actions, especially when there's context.",
            explanation: "When context makes the future clear (like 'later' or 'tomorrow'), Portuguese commonly uses present tense instead of future tense. This is natural and not considered incorrect.",
            pattern: "[present tense verb] + [future time word]",
            examples: [
              {
                target: "Voltamos mais tarde.",
                native: "We'll come back later.",
                breakdown: "voltamos (we return - present) + mais tarde (later) = future meaning"
              },
              {
                target: "Está bem, esperamos.",
                native: "That's fine, we'll wait.",
                breakdown: "esperamos (we wait - present) used for future 'we will wait'"
              }
            ],
            thinkingTip: "If you say WHEN something will happen, you can use present tense. 'Voltamos amanhã' = 'We return tomorrow' = 'We'll return tomorrow'.",
            commonMistake: "Don't overthink future tense – present tense with time context is often more natural."
          }
        ]
      }
    ],
    phrases: [
      {
        phrase: "Uma mesa para dois, se faz favor.",
        translation: "A table for two, please.",
        context: "Asking for a table",
      },
      {
        phrase: "O que me recomenda?",
        translation: "What do you recommend?",
        context: "Asking for suggestions",
      },
      {
        phrase: "Para mim, o peixe.",
        translation: "For me, the fish.",
        context: "Ordering food",
      },
    ],
    dialogNodes: [
      {
        id: "start",
        type: "start",
        text: "Bom dia, têm mesa disponível?",
        translation: "Good morning, do you have a table available?",
        children: [
          { id: "yes-table" },
          { id: "wait-time" },
          { id: "reservation-needed" },
        ],
      },
      {
        id: "yes-table",
        type: "option",
        text: "Sim, para quantas pessoas?",
        translation: "Yes, for how many people?",
        children: [
          { id: "two-people" },
          { id: "group" },
        ],
      },
      {
        id: "wait-time",
        type: "option",
        text: "Há uma espera de 15 minutos.",
        translation: "There's a 15-minute wait.",
        children: [
          { id: "accept-wait" },
          { id: "come-back" },
        ],
      },
      {
        id: "reservation-needed",
        type: "option",
        text: "Tem reserva?",
        translation: "Do you have a reservation?",
        children: [
          { id: "no-reservation" },
          { id: "yes-reservation" },
        ],
      },
      {
        id: "two-people",
        type: "response",
        text: "Para duas pessoas, se faz favor.",
        translation: "For two people, please.",
        children: [{ id: "seated" }],
      },
      {
        id: "group",
        type: "response",
        text: "Somos um grupo de cinco.",
        translation: "We're a group of five.",
        children: [
          { id: "seated" },
          { id: "wait-time" },
        ],
      },
      {
        id: "accept-wait",
        type: "response",
        text: "Está bem, esperamos.",
        translation: "That's fine, we'll wait.",
        children: [{ id: "seated" }],
      },
      {
        id: "come-back",
        type: "response",
        text: "Voltamos mais tarde.",
        translation: "We'll come back later.",
        children: [{ id: "end-leave" }],
      },
      {
        id: "no-reservation",
        type: "response",
        text: "Não, é necessário?",
        translation: "No, is it necessary?",
        children: [
          { id: "yes-table" },
          { id: "wait-time" },
        ],
      },
      {
        id: "yes-reservation",
        type: "response",
        text: "Sim, em nome de Silva.",
        translation: "Yes, under the name Silva.",
        children: [{ id: "seated" }],
      },
      {
        id: "seated",
        type: "option",
        text: "Por aqui, se faz favor. Aqui está a ementa.",
        translation: "This way, please. Here's the menu.",
        children: [{ id: "thank-seated" }],
      },
      {
        id: "thank-seated",
        type: "response",
        text: "Obrigado.",
        translation: "Thank you.",
        children: [{ id: "end-success" }],
      },
      {
        id: "end-success",
        type: "end",
        text: "Bom apetite!",
        translation: "Enjoy your meal!",
      },
      {
        id: "end-leave",
        type: "end",
        text: "Até logo.",
        translation: "See you later.",
      },
    ],
  },
  {
    id: "asking-directions",
    title: "Asking for Directions",
    description: "Navigate and ask for directions",
    language: "pt-PT",
    difficulty: "intermediate",
    category: "transport",
    vocabulary: {
      words: [
        {
          baseForm: "chegar",
          translation: "to arrive / to get to",
          pronunciation: "sheh-GAR",
          forms: [
            { form: "chegar", usage: "infinitive - to arrive" },
            { form: "chego", usage: "I arrive / I get to" },
            { form: "chega", usage: "you arrive (formal) / he/she arrives" },
            { form: "chegamos", usage: "we arrive" },
          ],
          examples: [
            { text: "Como é que chego ao museu?", translation: "How do I get to the museum?" },
            { text: "Chega lá em cinco minutos.", translation: "You'll get there in five minutes." },
          ],
        },
        {
          baseForm: "virar",
          translation: "to turn",
          pronunciation: "vee-RAR",
          forms: [
            { form: "virar", usage: "infinitive - to turn" },
            { form: "vire", usage: "turn! (imperative)" },
            { form: "vira", usage: "he/she turns" },
          ],
          examples: [
            { text: "Siga em frente e depois vire à direita.", translation: "Go straight and then turn right." },
            { text: "Na rotunda, vire à esquerda.", translation: "At the roundabout, turn left." },
          ],
        },
        {
          baseForm: "seguir",
          translation: "to follow / to continue",
          pronunciation: "seh-GEER",
          forms: [
            { form: "seguir", usage: "infinitive - to follow/continue" },
            { form: "siga", usage: "go! / continue! (imperative)" },
            { form: "sigo", usage: "I follow / I continue" },
          ],
          examples: [
            { text: "Siga em frente e depois vire à direita.", translation: "Go straight and then turn right." },
          ],
        },
        {
          baseForm: "apanhar",
          translation: "to catch / to take (transport)",
          pronunciation: "ah-pah-NYAR",
          forms: [
            { form: "apanhar", usage: "infinitive - to catch/take" },
            { form: "apanhe", usage: "catch! / take! (imperative)" },
            { form: "apanho", usage: "I catch / I take" },
          ],
          examples: [
            { text: "É melhor apanhar o autocarro.", translation: "It's better to take the bus." },
            { text: "Que autocarro devo apanhar?", translation: "Which bus should I take?" },
          ],
        },
        {
          baseForm: "procurar",
          translation: "to look for / to search",
          pronunciation: "proh-koo-RAR",
          forms: [
            { form: "procurar", usage: "infinitive - to look for" },
            { form: "procura", usage: "you look for (formal) / he/she looks for" },
            { form: "procuro", usage: "I look for" },
          ],
          examples: [
            { text: "Que museu procura?", translation: "Which museum are you looking for?" },
          ],
        },
        {
          baseForm: "perguntar",
          translation: "to ask",
          pronunciation: "pehr-goon-TAR",
          forms: [
            { form: "perguntar", usage: "infinitive - to ask" },
            { form: "pergunto", usage: "I ask" },
            { form: "pergunte", usage: "ask! (imperative)" },
          ],
          examples: [
            { text: "Vou perguntar a outra pessoa.", translation: "I'll ask someone else." },
          ],
        },
      ],
      expressions: [
        {
          expression: "como é que chego",
          translation: "how do I get to",
          literal: "how is it that I arrive",
          components: [
            { word: "como", meaning: "how", note: "interrogative adverb" },
            { word: "é que", meaning: "is it that", note: "emphatic question construction" },
            { word: "chego", meaning: "I arrive / I get", note: "1st person singular of 'chegar'" },
          ],
          examples: [
            { text: "Desculpe, como é que chego ao museu?", translation: "Excuse me, how do I get to the museum?" },
          ],
        },
        {
          expression: "é longe daqui",
          translation: "is it far from here",
          literal: "is far from here",
          components: [
            { word: "é", meaning: "is", note: "3rd person singular of 'ser'" },
            { word: "longe", meaning: "far", note: "adverb" },
            { word: "daqui", meaning: "from here", note: "contraction of 'de' + 'aqui'" },
          ],
          examples: [
            { text: "É longe daqui?", translation: "Is it far from here?" },
          ],
        },
        {
          expression: "a pé",
          translation: "on foot / walking",
          literal: "by foot",
          components: [
            { word: "a", meaning: "by / on", note: "preposition" },
            { word: "pé", meaning: "foot", note: "noun (masculine)" },
          ],
          examples: [
            { text: "São só cinco minutos a pé.", translation: "It's only five minutes on foot." },
            { text: "Pode ir a pé.", translation: "You can go on foot." },
          ],
        },
        {
          expression: "não sou daqui",
          translation: "I'm not from here",
          literal: "not am from here",
          components: [
            { word: "não", meaning: "not / no", note: "negation" },
            { word: "sou", meaning: "I am", note: "1st person singular of 'ser'" },
            { word: "daqui", meaning: "from here", note: "contraction of 'de' + 'aqui'" },
          ],
          examples: [
            { text: "Desculpe, não sou daqui.", translation: "Sorry, I'm not from here." },
          ],
        },
        {
          expression: "obrigado na mesma",
          translation: "thanks anyway",
          literal: "thank you in the same",
          components: [
            { word: "obrigado", meaning: "thank you / thanks", note: "masculine form (use 'obrigada' if female)" },
            { word: "na", meaning: "in the", note: "contraction of 'em' + 'a'" },
            { word: "mesma", meaning: "same", note: "feminine form of 'mesmo'" },
          ],
          examples: [
            { text: "Obrigado na mesma.", translation: "Thanks anyway." },
          ],
        },
        {
          expression: "é melhor",
          translation: "it's better to",
          literal: "is better",
          components: [
            { word: "é", meaning: "is", note: "3rd person singular of 'ser'" },
            { word: "melhor", meaning: "better / best", note: "comparative/superlative adjective" },
          ],
          examples: [
            { text: "É melhor apanhar o autocarro.", translation: "It's better to take the bus." },
          ],
        },
      ],
      topical: [
        {
          category: "Directions",
          items: [
            { word: "direita", translation: "right", pronunciation: "dee-RAY-tah" },
            { word: "esquerda", translation: "left", pronunciation: "esh-KEHR-dah" },
            { word: "em frente", translation: "straight ahead", pronunciation: "em FREHN-teh" },
            { word: "atrás", translation: "behind", pronunciation: "ah-TRAHSH" },
          ],
        },
        {
          category: "Places & Landmarks",
          items: [
            { word: "esquina", translation: "corner", pronunciation: "esh-KEE-nah" },
            { word: "rua", translation: "street", pronunciation: "HOO-ah" },
            { word: "paragem", translation: "bus stop", pronunciation: "pah-RAH-zhehm" },
            { word: "semáforo", translation: "traffic light", pronunciation: "seh-MAH-foh-roo" },
            { word: "rotunda", translation: "roundabout", pronunciation: "hoh-TOON-dah" },
            { word: "museu", translation: "museum", pronunciation: "moo-ZEH-oo" },
          ],
        },
        {
          category: "Distance & Time",
          items: [
            { word: "longe", translation: "far", pronunciation: "LOHN-zheh" },
            { word: "perto", translation: "near/close", pronunciation: "PEHR-too" },
            { word: "minutos", translation: "minutes", pronunciation: "mee-NOO-toosh" },
          ],
        },
        {
          category: "Transport",
          items: [
            { word: "autocarro", translation: "bus", pronunciation: "ow-toh-KAH-hoo" },
            { word: "metro", translation: "subway", pronunciation: "MEH-troo" },
            { word: "táxi", translation: "taxi", pronunciation: "TAK-see" },
          ],
        },
      ],
    },
    grammar: [
      {
        category: "Questions",
        rules: [
          {
            id: "como-e-que",
            title: "The 'Como é que' Construction",
            summary: "'É que' is an emphatic question marker – it adds emphasis and is very common in European Portuguese.",
            explanation: "In European Portuguese, 'é que' is inserted in questions for emphasis. It literally means 'is it that' but functions as a question intensifier. 'Como chego?' and 'Como é que chego?' both mean 'How do I get?' but the second is more natural in Portugal.",
            pattern: "Question word + é que + [rest of sentence]",
            examples: [
              {
                target: "Como é que chego ao museu?",
                native: "How do I get to the museum?",
                breakdown: "Como (how) + é que (emphatic) + chego (I arrive) = How do I get?"
              },
              {
                target: "Que museu procura?",
                native: "Which museum are you looking for?",
                breakdown: "Can also be 'Que museu é que procura?' with emphasis"
              }
            ],
            thinkingTip: "Think of 'é que' as invisible in translation – it just makes the question sound more natural to Portuguese ears. You can always add it after the question word.",
            commonMistake: "Don't try to translate 'é que' literally. It's not asking 'is it that...?' – it's just how questions are phrased naturally."
          }
        ]
      },
      {
        category: "Imperatives (Commands)",
        rules: [
          {
            id: "imperative-directions",
            title: "Imperative for Giving Directions",
            summary: "Use imperative verb forms (commands) when giving directions. They're polite in context.",
            explanation: "When giving directions, Portuguese uses imperative forms which might sound like commands but are normal and polite. 'Siga' (go/follow), 'Vire' (turn), 'Atravesse' (cross) are all imperative forms.",
            pattern: "Imperative verb + direction/location",
            examples: [
              {
                target: "Siga em frente",
                native: "Go straight ahead",
                breakdown: "Siga (go! - formal imperative of 'seguir') + em frente (ahead)"
              },
              {
                target: "Vire à direita",
                native: "Turn right",
                breakdown: "Vire (turn! - formal imperative of 'virar') + à direita (to the right)"
              },
              {
                target: "Depois vire à esquerda",
                native: "Then turn left",
                breakdown: "depois (then) + vire (turn!) + à esquerda (to the left)"
              }
            ],
            thinkingTip: "Directions use 'command' forms but they're not rude – it's just how directions work. Think of it like GPS instructions.",
            commonMistake: "Don't soften imperatives too much – 'Vire à direita' is perfectly polite for directions."
          }
        ]
      },
      {
        category: "Prepositions",
        rules: [
          {
            id: "a-direction",
            title: "'À' for Direction (to the)",
            summary: "'À' is a contraction of 'a' (to) + 'a' (the, feminine). Used with directions and destinations.",
            explanation: "When indicating direction, use 'a' (to) + article. For feminine nouns like 'direita' (right), 'esquerda' (left), this becomes 'à'. For masculine nouns, it's 'ao'.",
            pattern: "a + a = à (feminine) | a + o = ao (masculine)",
            examples: [
              {
                target: "Vire à direita",
                native: "Turn to the right",
                breakdown: "à = a (to) + a (the, fem.) because 'direita' is feminine"
              },
              {
                target: "Vire à esquerda",
                native: "Turn to the left",
                breakdown: "à = a + a because 'esquerda' is feminine"
              },
              {
                target: "Chego ao museu",
                native: "I get to the museum",
                breakdown: "ao = a (to) + o (the, masc.) because 'museu' is masculine"
              }
            ],
            thinkingTip: "Right and left are feminine in Portuguese (a direita, a esquerda). So it's always 'à direita' and 'à esquerda'.",
            commonMistake: "Don't say 'vire a direita' – the contraction 'à' is required."
          },
          {
            id: "de-contractions",
            title: "Contractions with 'De' (of/from)",
            summary: "'De' contracts with articles and 'aqui' to form 'do/da/daqui'.",
            explanation: "'De' (of/from) combines with articles: de + o = do, de + a = da. It also combines with 'aqui' (here): de + aqui = daqui (from here).",
            pattern: "de + o = do | de + a = da | de + aqui = daqui",
            examples: [
              {
                target: "É longe daqui?",
                native: "Is it far from here?",
                breakdown: "daqui = de (from) + aqui (here)"
              },
              {
                target: "Não sou daqui",
                native: "I'm not from here",
                breakdown: "daqui = from here (de + aqui)"
              }
            ],
            thinkingTip: "Whenever you want to say 'from here', it's one word: 'daqui'. Same pattern: 'from there' = 'dali' (de + ali).",
            commonMistake: "Don't say 'de aqui' – always contract to 'daqui'."
          }
        ]
      },
      {
        category: "Ser vs Estar",
        rules: [
          {
            id: "ser-location",
            title: "Ser for Permanent Location",
            summary: "Use 'ser' for location of places/buildings (they don't move). Use 'estar' for people/things that could move.",
            explanation: "'Ser' is used for permanent characteristics. Buildings and landmarks have fixed locations, so we use 'ser' to describe where they are. For distance, 'ser' is also used because distance is a characteristic.",
            pattern: "Location of place/distance = ser | Location of person/movable thing = estar",
            examples: [
              {
                target: "É longe daqui?",
                native: "Is it far from here?",
                breakdown: "É (it is - ser) for distance as a characteristic"
              },
              {
                target: "É um bocado longe",
                native: "It's a bit far",
                breakdown: "Using 'ser' because distance is a fixed characteristic of the route"
              },
              {
                target: "O museu é na praça",
                native: "The museum is in the square",
                breakdown: "Using 'ser' because the museum's location is permanent"
              }
            ],
            thinkingTip: "Ask: Can this thing move? Buildings = ser. People, cars, temporary things = estar.",
            commonMistake: "Don't use 'estar' for building locations – 'O museu está...' sounds like it might move!"
          }
        ]
      },
      {
        category: "Expressing Distance & Time",
        rules: [
          {
            id: "a-pe",
            title: "'A pé' and Transportation Expressions",
            summary: "'A pé' means 'on foot'. Use 'de' + transport for other methods.",
            explanation: "For 'on foot', use 'a pé'. For other transportation, typically use 'de': de autocarro (by bus), de metro (by subway), de táxi (by taxi). But 'apanhar' (to catch) is used differently.",
            pattern: "a pé (on foot) | de [transport] (by [transport]) | apanhar o [transport] (take the)",
            examples: [
              {
                target: "São cinco minutos a pé",
                native: "It's five minutes on foot",
                breakdown: "a pé = on foot (by foot)"
              },
              {
                target: "É melhor apanhar o autocarro",
                native: "It's better to take the bus",
                breakdown: "apanhar (to catch/take) + o autocarro (the bus)"
              }
            ],
            thinkingTip: "'A pé' is fixed – memorize it as one unit. For vehicles, you 'catch' (apanhar) them like catching a ride.",
            commonMistake: "Don't say 'de pé' for walking – 'de pé' means 'standing up'! Use 'a pé' for 'on foot'."
          }
        ]
      }
    ],
    phrases: [
      {
        phrase: "Como é que chego a...?",
        translation: "How do I get to...?",
        context: "Asking for directions",
      },
      {
        phrase: "É longe daqui?",
        translation: "Is it far from here?",
        context: "Asking about distance",
      },
      {
        phrase: "Pode repetir, se faz favor?",
        translation: "Can you repeat, please?",
        context: "Asking for clarification",
      },
    ],
    dialogNodes: [
      {
        id: "start",
        type: "start",
        text: "Desculpe, como é que chego ao museu?",
        translation: "Excuse me, how do I get to the museum?",
        children: [
          { id: "give-directions" },
          { id: "dont-know" },
          { id: "ask-which-museum" },
        ],
      },
      {
        id: "give-directions",
        type: "option",
        text: "Siga em frente e depois vire à direita.",
        translation: "Go straight and then turn right.",
        children: [
          { id: "thank-you" },
          { id: "ask-distance" },
          { id: "ask-repeat" },
        ],
      },
      {
        id: "dont-know",
        type: "option",
        text: "Desculpe, não sou daqui.",
        translation: "Sorry, I'm not from here.",
        children: [
          { id: "thank-anyway" },
          { id: "ask-someone-else" },
        ],
      },
      {
        id: "ask-which-museum",
        type: "option",
        text: "Que museu procura?",
        translation: "Which museum are you looking for?",
        children: [{ id: "specify-museum" }],
      },
      {
        id: "thank-you",
        type: "response",
        text: "Muito obrigado.",
        translation: "Thank you very much.",
        children: [{ id: "end-success" }],
      },
      {
        id: "ask-distance",
        type: "response",
        text: "É longe daqui?",
        translation: "Is it far from here?",
        children: [
          { id: "distance-near" },
          { id: "distance-far" },
        ],
      },
      {
        id: "ask-repeat",
        type: "response",
        text: "Pode repetir, se faz favor?",
        translation: "Can you repeat, please?",
        children: [{ id: "give-directions" }],
      },
      {
        id: "thank-anyway",
        type: "response",
        text: "Obrigado na mesma.",
        translation: "Thanks anyway.",
        children: [
          { id: "ask-someone-else" },
          { id: "end-fail" },
        ],
      },
      {
        id: "ask-someone-else",
        type: "response",
        text: "Vou perguntar a outra pessoa.",
        translation: "I'll ask someone else.",
        children: [{ id: "start" }],
      },
      {
        id: "specify-museum",
        type: "response",
        text: "O Museu de Arte Moderna.",
        translation: "The Museum of Modern Art.",
        children: [
          { id: "give-directions" },
          { id: "dont-know" },
        ],
      },
      {
        id: "distance-near",
        type: "option",
        text: "Não, são só cinco minutos a pé.",
        translation: "No, it's only five minutes on foot.",
        children: [{ id: "thank-you" }],
      },
      {
        id: "distance-far",
        type: "option",
        text: "É um bocado longe. É melhor apanhar o autocarro.",
        translation: "It's a bit far. Better to take the bus.",
        children: [
          { id: "thank-you" },
          { id: "ask-bus" },
        ],
      },
      {
        id: "ask-bus",
        type: "response",
        text: "Que autocarro devo apanhar?",
        translation: "Which bus should I take?",
        children: [{ id: "bus-info" }],
      },
      {
        id: "bus-info",
        type: "option",
        text: "O número 28, para ali na paragem.",
        translation: "Number 28, it stops over there.",
        children: [{ id: "thank-you" }],
      },
      {
        id: "end-success",
        type: "end",
        text: "De nada, boa viagem!",
        translation: "You're welcome, have a good trip!",
      },
      {
        id: "end-fail",
        type: "end",
        text: "Desculpe não poder ajudar.",
        translation: "Sorry I couldn't help.",
      },
    ],
  },
];

export function getTopicById(id: string): DialogTopic | undefined {
  return sampleTopics.find((topic) => topic.id === id);
}

export function buildDialogTree(
  nodes: DialogNode[]
): Map<string, DialogNode[]> {
  const tree = new Map<string, DialogNode[]>();

  nodes.forEach((node) => {
    if (node.children) {
      const childNodes = node.children
        .map((edge) => nodes.find((n) => n.id === edge.id))
        .filter((n): n is DialogNode => n !== undefined);
      tree.set(node.id, childNodes);
    }
  });

  return tree;
}

/**
 * Merge consecutive same-side (non-player) nodes into one.
 * When the LLM produces two NPC nodes in a row before a player turn,
 * fold the extra node's text into the first node's alternatives ("also say").
 */
export function mergeConsecutiveNpcNodes(nodes: DialogNode[]): DialogNode[] {
  const validNodes = nodes.filter((n): n is DialogNode => n != null);
  const byId = new Map(validNodes.map((n) => [n.id, n]));
  const absorbed = new Set<string>();

  const merged = validNodes.map((node) => {
    if (absorbed.has(node.id)) return null;

    // For option/end nodes, only apply empty-text promotion (no merging)
    if (node.type === "option" || node.type === "end") {
      return promoteAlternativeIfEmpty(node);
    }

    let current = { ...node, alternatives: [...(node.alternatives ?? [])] };

    // Walk forward while the only child is also an NPC node
    while (current.children?.length === 1) {
      const childId = current.children[0]!.id;
      const child = byId.get(childId);
      if (!child || child.type === "option" || child.type === "end") break;

      // Absorb child: add its text as an alternative
      current.alternatives.push({
        text: child.text,
        translation: child.translation,
      });
      // Also carry over child's own alternatives
      if (child.alternatives) {
        current.alternatives.push(...child.alternatives);
      }
      // Re-point to child's children
      current.children = child.children ? [...child.children] : undefined;
      absorbed.add(childId);
    }

    // Cap at 3 alternatives after merging consecutive NPC nodes
    const cappedAlts = current.alternatives.slice(0, 3);
    const result = {
      ...current,
      alternatives: cappedAlts.length > 0 ? cappedAlts : undefined,
    };

    return promoteAlternativeIfEmpty(result);
  });

  return merged.filter((n): n is DialogNode => n !== null);
}

/** If a node has empty text but non-empty alternatives, promote the first alternative. */
function promoteAlternativeIfEmpty(node: DialogNode): DialogNode {
  if (node.text || !node.alternatives?.length) return node;

  const [first, ...rest] = node.alternatives;
  if (!first) return node;
  return {
    ...node,
    text: first.text,
    translation: first.translation,
    alternatives: rest.length > 0 ? rest : undefined,
  };
}

